import { getPublicKeyAsync, signAsync, utils } from "@noble/ed25519";
import { fetchWithAuth } from "../lib/api";

type StoredIdentity = {
  version: 1;
  deviceId: string;
  publicKey: string;
  privateKey: string;
  createdAtMs: number;
};

export type DeviceIdentity = {
  deviceId: string;
  publicKey: string;
  privateKey: string;
};

const STORAGE_KEY = "command-device-identity-v1";
const DEVICE_IDENTITY_API_PATH = "/api/me/device-identity";
let inMemoryIdentity: DeviceIdentity | null = null;
let inFlightIdentity: Promise<DeviceIdentity> | null = null;

function base64UrlEncode(bytes: Uint8Array): string {
  let binary = "";
  for (const byte of bytes) {
    binary += String.fromCharCode(byte);
  }
  return btoa(binary).replaceAll("+", "-").replaceAll("/", "_").replace(/=+$/g, "");
}

function base64UrlDecode(input: string): Uint8Array {
  const normalized = input.replaceAll("-", "+").replaceAll("_", "/");
  const padded = normalized + "=".repeat((4 - (normalized.length % 4)) % 4);
  const binary = atob(padded);
  const out = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) {
    out[i] = binary.charCodeAt(i);
  }
  return out;
}

function bytesToHex(bytes: Uint8Array): string {
  return Array.from(bytes)
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}

async function fingerprintPublicKey(publicKey: Uint8Array): Promise<string> {
  const hash = await crypto.subtle.digest("SHA-256", publicKey as Uint8Array<ArrayBuffer>);
  return bytesToHex(new Uint8Array(hash));
}

async function generateIdentity(): Promise<DeviceIdentity> {
  const privateKey = utils.randomPrivateKey();
  const publicKey = await getPublicKeyAsync(privateKey);
  const deviceId = await fingerprintPublicKey(publicKey);
  return {
    deviceId,
    publicKey: base64UrlEncode(publicKey),
    privateKey: base64UrlEncode(privateKey),
  };
}

function isStoredIdentity(value: unknown): value is StoredIdentity {
  if (!value || typeof value !== "object") {
    return false;
  }
  const parsed = value as Partial<StoredIdentity>;
  return (
    parsed.version === 1 &&
    typeof parsed.deviceId === "string" &&
    typeof parsed.publicKey === "string" &&
    typeof parsed.privateKey === "string" &&
    typeof parsed.createdAtMs === "number"
  );
}

function loadStoredIdentityFromLocalStorage(): StoredIdentity | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return null;
    }
    const parsed = JSON.parse(raw) as unknown;
    return isStoredIdentity(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

function saveStoredIdentityToLocalStorage(stored: StoredIdentity): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(stored));
  } catch {
    // ignore storage failures
  }
}

async function fetchStoredIdentityFromApi(): Promise<StoredIdentity | null> {
  try {
    const response = await fetchWithAuth(DEVICE_IDENTITY_API_PATH, {
      method: "GET",
      headers: {
        Accept: "application/json",
      },
    });
    if (!response.ok) {
      return null;
    }
    const parsed = (await response.json()) as { data?: unknown };
    return isStoredIdentity(parsed?.data) ? parsed.data : null;
  } catch {
    return null;
  }
}

async function saveStoredIdentityToApi(stored: StoredIdentity): Promise<void> {
  try {
    await fetchWithAuth(DEVICE_IDENTITY_API_PATH, {
      method: "PUT",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify(stored),
    });
  } catch {
    // ignore network/API failures; local fallback still works
  }
}

async function ensureConsistentDeviceId(stored: StoredIdentity): Promise<StoredIdentity> {
  const derivedId = await fingerprintPublicKey(base64UrlDecode(stored.publicKey));
  if (derivedId === stored.deviceId) {
    return stored;
  }
  return {
    ...stored,
    deviceId: derivedId,
  };
}

export async function loadOrCreateDeviceIdentity(): Promise<DeviceIdentity> {
  if (inMemoryIdentity) {
    return inMemoryIdentity;
  }
  if (inFlightIdentity) {
    return await inFlightIdentity;
  }

  inFlightIdentity = (async () => {
    const apiStored = await fetchStoredIdentityFromApi();
    const localStored = loadStoredIdentityFromLocalStorage();
    let stored = apiStored ?? localStored;

    if (!stored) {
      const generated = await generateIdentity();
      stored = {
        version: 1,
        deviceId: generated.deviceId,
        publicKey: generated.publicKey,
        privateKey: generated.privateKey,
        createdAtMs: Date.now(),
      };
    }

    const normalized = await ensureConsistentDeviceId(stored);
    saveStoredIdentityToLocalStorage(normalized);
    await saveStoredIdentityToApi(normalized);

    inMemoryIdentity = {
      deviceId: normalized.deviceId,
      publicKey: normalized.publicKey,
      privateKey: normalized.privateKey,
    };
    return inMemoryIdentity;
  })();

  try {
    return await inFlightIdentity;
  } finally {
    inFlightIdentity = null;
  }
}

export async function signDevicePayload(privateKeyBase64Url: string, payload: string) {
  const key = base64UrlDecode(privateKeyBase64Url);
  const data = new TextEncoder().encode(payload);
  const sig = await signAsync(data, key);
  return base64UrlEncode(sig);
}
